import React, { useState, useEffect, useCallback, useMemo, createContext, StrictMode, useRef } from 'react';
import { SafeAreaView, Platform, Text, View, Linking, AppState, Animated } from 'react-native';
import useCachedResources from '@hooks/useCachedResources';
// import useColorScheme from '@hooks/useColorScheme';
import Navigation from './src/navigation';
import { Provider } from 'react-redux';
import { store, persistor } from '@reduxLocal/persistState';
import { PersistGate } from 'redux-persist/integration/react';
import "./i18n.config";
import { bootstrapI18nLanguage } from './i18n.config';
import { applyMomentLocale } from './src/utils/momentLocale';
import i18n from 'i18next';
import { QueryClientProvider } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { createQueryClientWithDemoSandbox } from '@api/utils/demoCompanyQueryClient';
// TensorFlow will be initialized lazily when needed to avoid startup errors
// import '@tensorflow/tfjs-react-native';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { TamaguiProvider } from '@tamagui/core';
import { config } from './tamagui.config';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { DefaultTheme } from '@react-navigation/native';
import * as WebBrowser from 'expo-web-browser';
import CrashInterceptor, { setupGlobalErrorHandlers } from '@utils/CrashInterceptor';
import { UtilitiesProvider } from './src/contexts/UtilitiesContext';
import { ToastProvider, useToast } from './src/contexts/ToastContext';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { useCompanyQueryInvalidation } from './src/hooks/useCompanyQueryInvalidation';
import UpdateToast from './src/components/toast/UpdateToast.web';
import { applyTheme as applyWebTheme, getInitialTheme as getInitialWebTheme, getStoredTheme, subscribeThemeChanges, type WebTheme } from './src/utils/webTheme';
import { SafeStripeTerminalProvider } from './src/providers/StripeTerminalProvider';
import { clearSessionCookie } from '@utils/sessionCookie';
import ThemeScrollbars from './src/components/Global/ThemeScrollbars';

// Robust auth session completion with QuotaExceededError recovery
try {
  WebBrowser.maybeCompleteAuthSession({ skipRedirectCheck: true });
} catch (e: any) {
  if (e?.name === 'QuotaExceededError' || e?.message?.includes('Quota') || e?.message?.includes('exceeded the quota')) {
    console.warn('QuotaExceededError caught during auth session completion, clearing persist:root and retrying...');
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem('persist:root');
        WebBrowser.maybeCompleteAuthSession({ skipRedirectCheck: true });
        console.log('Successfully recovered from QuotaExceededError');
      } catch (e2) {
        console.warn('Retry failed after clearing persist:root:', e2);
      }
    }
  } else {
    console.warn('maybeCompleteAuthSession threw an error:', e);
  }
}

// Robust fallback to ensure popup window closes on web and notifies the opener
if (Platform.OS === 'web' && typeof window !== 'undefined') {
  try {
    const { completeOAuthPopupSessionIfNeeded } = require('./src/utils/oauthWebPopupCompletion');
    completeOAuthPopupSessionIfNeeded();
  } catch (e) {
    console.warn('Custom auth session completion failed:', e);
  }
}

export const ColorSchemeContext = createContext<{
  colorScheme: WebTheme;
  setColorScheme: (scheme: WebTheme) => void;
}>({
  colorScheme: 'light',
  setColorScheme: () => {},
});

export const useColorScheme = () => React.useContext(ColorSchemeContext);

// Helper functions for service worker and notifications
let updateNotificationShown = false; // Prevent duplicate notifications
let hasServiceWorkerUpdate = false; // Track if SW update is available
let showToastCallback: ((message: string, actionLabel?: string, onAction?: () => void) => void) | null = null; // Toast callback function
let serviceWorkerRegistered = false; // Track if service worker is already registered

// Set up update listeners for a service worker registration
const setupServiceWorkerListeners = (registration: ServiceWorkerRegistration) => {
  // Handle updates with debouncing
  let updateCheckTimeout: NodeJS.Timeout | null = null;

  registration.addEventListener('updatefound', () => {
    const newWorker = registration.installing;
    if (newWorker) {
      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New service worker is available
          console.log('New service worker available');
          hasServiceWorkerUpdate = true;

          // Debounce update notifications (prevent spam)
          if (updateCheckTimeout) {
            clearTimeout(updateCheckTimeout);
          }

          updateCheckTimeout = setTimeout(() => {
            // Only show notification once per session
            if (!updateNotificationShown) {
              showUpdateNotification();
            }
          }, 2000); // Wait 2 seconds before showing notification
        }
      });
    }
  });

  // Handle service worker messages - don't auto-reload
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'SKIP_WAITING') {
      console.log('Service worker ready to activate');
      // Don't auto-reload, let user decide via notification click
      // Prevent immediate refresh by not calling window.location.reload()
    }
  });
};

const isLocalDevHost = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
};

const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.log('Service Worker not supported');
    return null;
  }

  // Dev server: SW cache-first can serve stale bundle.js — always use network.
  if (__DEV__ && isLocalDevHost()) {
    try {
      const existing = await navigator.serviceWorker.getRegistration();
      if (existing) {
        await existing.unregister();
        console.log('Unregistered service worker on localhost (dev cache bypass)');
      }
    } catch (error) {
      console.warn('Failed to unregister service worker on localhost:', error);
    }
    return null;
  }

  // Prevent multiple registrations
  if (serviceWorkerRegistered) {
    console.log('Service Worker already registered, skipping...');
    return null;
  }

  try {
    // Check if service worker is already registered
    const existing = await navigator.serviceWorker.getRegistration();
    if (existing) {
      console.log('Service Worker already exists, using existing registration');
      serviceWorkerRegistered = true;
      setupServiceWorkerListeners(existing);
      return existing;
    }

    // Register the service worker with proper path
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none', // Always fetch latest SW
    });

    serviceWorkerRegistered = true;
    console.log('Service Worker registered successfully:', registration.scope);
    setupServiceWorkerListeners(registration);

    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    serviceWorkerRegistered = false; // Reset on error
    return null;
  }
};

const showUpdateNotification = () => {
  // Prevent showing notification multiple times
  if (updateNotificationShown) {
    console.log('Update notification already shown, skipping...');
    return;
  }

  updateNotificationShown = true;

  // Show in-app toast notification (primary method for web)
  if (showToastCallback) {
    showToastCallback(
      i18n.t('appUpdateToastMessage'),
      i18n.t('appUpdateToastReload'),
      () => {
        console.log('User confirmed app update, reloading...');
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          window.location.reload();
        }
      }
    );
  }

  // Also show browser notification if permission is granted (optional enhancement)
  if ('Notification' in window && Notification.permission === 'granted') {
    const notification = new Notification(i18n.t('appUpdateNotificationTitle'), {
      body: i18n.t('appUpdateNotificationBody'),
      tag: 'app-update',
      requireInteraction: true,
    });

    notification.onclick = () => {
      // Only reload when user explicitly clicks
      if (hasServiceWorkerUpdate) {
        console.log('User confirmed app update, reloading...');
        window.location.reload();
      }
      notification.close();
    };

    // Auto-close after 30 seconds if no interaction
    const timeoutId = setTimeout(() => {
      if (notification) {
        notification.close();
        updateNotificationShown = false; // Allow showing again later if needed
      }
    }, 30000);

    // Clear timeout if notification is closed manually
    notification.onclose = () => {
      clearTimeout(timeoutId);
    };
  }
};

const checkForServiceWorkerUpdates = async () => {
  if (!('serviceWorker' in navigator)) return;

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      if (!hasServiceWorkerUpdate) {
        await registration.update();
      }
    }
  } catch (error) {
    console.error('Service worker update check failed:', error);
  }
};

// Fallback: fetch version.json (cache-busted) to detect new deploy when SW serves stale content
const checkVersionFromServer = async () => {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return;
    const { version } = await res.json();
    const stored = sessionStorage.getItem('_appVersion');
    if (stored && stored !== version) {
      hasServiceWorkerUpdate = true;
      showUpdateNotification();
    }
    sessionStorage.setItem('_appVersion', version || '');
  } catch {
    // version.json may not exist in dev or old builds
  }
};

const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission;
  }

  return Notification.permission;
};

const sendWelcomeNotification = () => {
  if ('Notification' in window && Notification.permission === 'granted') {
    const notification = new Notification('Welcome to SmartSaaS!', {
      body: 'You\'re all set up and ready to go.',
      tag: 'welcome',
      requireInteraction: false,
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    // Auto close after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);
  }
};

const sendDevelopmentModeNotification = () => {
  if ('Notification' in window && Notification.permission === 'granted') {
    const notification = new Notification('🚧 Development Mode Active', {
      body: 'You\'re running SmartSaaS in development mode. Some features may behave differently.',
      tag: 'dev-mode',
      requireInteraction: true, // Keep visible until user interacts
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    // Auto close after 10 seconds in dev mode
    setTimeout(() => {
      notification.close();
    }, 10000);
  }
};

// Wrapper component to use hooks inside QueryClientProvider
const AppContent = ({ showUpdateDetail }: { showUpdateDetail: boolean }) => {
  React.useEffect(() => {
    const onLanguageChanged = (lng: string) => {
      void applyMomentLocale(lng);
    };
    i18n.on('languageChanged', onLanguageChanged);
    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, []);

  // Global hook that watches companyNumber and invalidates queries automatically
  useCompanyQueryInvalidation();

  // Get toast function and expose it globally for service worker
  const { showToast } = useToast();
  const serviceWorkerInitRef = React.useRef(false);
  const updateCheckIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const focusHandlerRef = React.useRef<(() => void) | null>(null);
  const visibilityHandlerRef = React.useRef<(() => void) | null>(null);

  // Expose toast function to service worker update detection
  React.useEffect(() => {
    if (Platform.OS !== 'web' || serviceWorkerInitRef.current) {
      if (Platform.OS === 'web') {
        showToastCallback = showToast;
      }
      return;
    }

    const isLocalDevHost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname.endsWith('.local'));

    if (isLocalDevHost) {
      showToastCallback = showToast;
      return;
    }

    serviceWorkerInitRef.current = true;
    showToastCallback = showToast;

      // Register service worker on web (only once)
      registerServiceWorker().then(() => {
        // Check for updates immediately after registration
        checkForServiceWorkerUpdates();
        checkVersionFromServer();

        const runUpdateChecks = () => {
          checkForServiceWorkerUpdates();
          checkVersionFromServer();
        };

        // Check for updates more frequently (every 60 seconds)
        updateCheckIntervalRef.current = setInterval(runUpdateChecks, 60 * 1000);

        // Also check when window gains focus (user returns to tab)
        focusHandlerRef.current = runUpdateChecks;

        // Check when page becomes visible (user switches back to tab)
        visibilityHandlerRef.current = () => {
          if (!document.hidden) runUpdateChecks();
        };

        if (focusHandlerRef.current) {
          window.addEventListener('focus', focusHandlerRef.current);
        }
        if (visibilityHandlerRef.current) {
          document.addEventListener('visibilitychange', visibilityHandlerRef.current);
        }
      }).catch(error => {
        console.error('Failed to register service worker:', error);
        serviceWorkerRegistered = false; // Reset on error so it can retry
        serviceWorkerInitRef.current = false;
      });

    // Cleanup on unmount
    return () => {
      if (updateCheckIntervalRef.current) {
        clearInterval(updateCheckIntervalRef.current);
        updateCheckIntervalRef.current = null;
      }
      if (focusHandlerRef.current) {
        window.removeEventListener('focus', focusHandlerRef.current);
        focusHandlerRef.current = null;
      }
      if (visibilityHandlerRef.current) {
        document.removeEventListener('visibilitychange', visibilityHandlerRef.current);
        visibilityHandlerRef.current = null;
      }
    };
  }, [showToast]);

  return <Container showUpdateDetail={showUpdateDetail} />;
};

const Container = React.memo(({ showUpdateDetail }: { showUpdateDetail: boolean }) => {
  const [colorScheme, setColorSchemeState] = useState<WebTheme>(() => {
    if (Platform.OS === 'web') return getInitialWebTheme();
    return 'light';
  });

  // Keep the DOM in sync (web-native theming)
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    // Persist only when user explicitly chose light/dark.
    // If they chose "system", we keep storage empty and just update the DOM.
    applyWebTheme(colorScheme, { persist: getStoredTheme() != null });
  }, [colorScheme]);

  // If system theme changes and the user hasn't chosen a stored theme, follow it.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    return subscribeThemeChanges((next) => {
      setColorSchemeState((prev) => (prev === next ? prev : next));
    });
  }, []);

  const setColorScheme = useCallback((scheme: WebTheme) => {
    setColorSchemeState(scheme);
  }, []);

  const contextValue = useMemo(
    () => ({ colorScheme, setColorScheme }),
    [colorScheme, setColorScheme]
  );

  return (
    <ColorSchemeContext.Provider value={contextValue}>
      <SafeStripeTerminalProvider logLevel="verbose">
        <Navigation colorScheme={colorScheme as any} hasUpdatePending={showUpdateDetail} />
      </SafeStripeTerminalProvider>
    </ColorSchemeContext.Provider>
  );
});



const compareAppVersion = async (version) => {
  try {
    // Skip version check if no version provided
    if (!version) {
      console.log('No app version provided, skipping version check');
      return false;
    }

    const storedVersion = localStorage.getItem('appVersion');
    
    // First time visitor - store version and continue
    if (!storedVersion) {
      localStorage.setItem('appVersion', version);
      console.log('First time visitor, storing version:', version);
      return false; // Don't show update on first visit
    }
    
    // Compare versions
    if (storedVersion !== version) {
      console.log(`App version changed: ${storedVersion} → ${version}`);
      
      // Clear sensitive data (but preserve version for comparison)
      clearSessionCookie();
      
      // Clear specific cached data but keep version info
      const itemsToKeep = ['appVersion'];
      const localStorage_backup = {};
      
      itemsToKeep.forEach(key => {
        localStorage_backup[key] = localStorage.getItem(key);
      });
      
      // Clear all localStorage
      localStorage.clear();
      
      // Restore important items
      Object.keys(localStorage_backup).forEach(key => {
        if (localStorage_backup[key] !== null) {
          localStorage.setItem(key, localStorage_backup[key]);
        }
      });
      
      // Update to new version
      localStorage.setItem('appVersion', version);
      
      return true; // App was updated
    } else {
      // Same version - no update needed
      console.log('App version unchanged:', version);
      return false;
    }
  } catch (error) {
    console.error('Error comparing app version: ', error);
    // On error, try to store current version to prevent repeated failures
    try {
      localStorage.setItem('appVersion', version);
    } catch (storageError) {
      console.error('Failed to store app version:', storageError);
    }
    return false; // Don't show update on error to prevent breaking the app
  }
};

const clearCache = () => {
  // Clear specific cache items instead of everything
  const keysToPreserve = ['appVersion', 'theme', 'language'];
  const backup = {};
  
  // Backup important keys
  keysToPreserve.forEach(key => {
    backup[key] = localStorage.getItem(key);
  });
  
  // Clear localStorage
  localStorage.clear();
  
  // Restore preserved keys
  Object.keys(backup).forEach(key => {
    if (backup[key] !== null) {
      localStorage.setItem(key, backup[key]);
    }
  });
  
  // Clear sessionStorage
  sessionStorage.clear();

};

export default function App() {
  const [updated, setUpdated] = useState(false);
  const [initialized, setInitialized] = useState(false);
  
  // SOLUTION: Create a single stable QueryClient instance that never changes
  const queryClient = useRef<QueryClient | undefined>(undefined);
  if (!queryClient.current) {
    queryClient.current = createQueryClientWithDemoSandbox({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          staleTime: 10 * 60 * 1000, // 10 minutes
          retry: false,
          gcTime: 15 * 60 * 1000, // 15 min cache (was cacheTime in v4); limits memory growth
        },
      },
    });
  }

  const renderCount = useRef(0);
  const hasInitialized = useRef(false);
  renderCount.current += 1;
  
  // SOLUTION: Only initialize ONCE using ref instead of useEffect
  if (!hasInitialized.current) {
    console.log('� INITIALIZING APP ONCE');
    hasInitialized.current = true;
    setInitialized(true);
  }

  
  // SOLUTION: Remove ALL useEffect that could cause re-renders
  // No useEffect at all - just static initialization

  // Removed useEffect timer from here. Moved to module level for hot-reload support.

  const clearWebCache = () => {
    if (Platform.OS === 'web') {
      // Clear browser LocalStorage
      localStorage.clear();
      // Clear browser SessionStorage
      sessionStorage.clear();
    }
  };


    return (
      <GestureHandlerRootView style={{height: '100dvh'} as any}>
        <SafeAreaProvider>
          <TamaguiProvider config={config}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Provider store={store}>
                <UtilitiesProvider>
                        <ToastProvider>
                          <QueryClientProvider client={queryClient.current!}>
                            {/* {
                        __DEV__ &&
                    <ReactQueryDevtools initialIsOpen={false} position='bottom-left' />
                      } */}
                            <PersistGate
                              loading={null}
                              persistor={persistor}
                              onBeforeLift={async () => {
                                const withTimeout = async <T,>(
                                  label: string,
                                  promise: Promise<T>,
                                  ms = 8000
                                ): Promise<T | undefined> => {
                                  let timer: ReturnType<typeof setTimeout> | undefined;
                                  try {
                                    return await Promise.race([
                                      promise,
                                      new Promise<undefined>((resolve) => {
                                        timer = setTimeout(() => {
                                          console.warn(`[startup] ${label} timed out after ${ms}ms`);
                                          resolve(undefined);
                                        }, ms);
                                      }),
                                    ]);
                                  } finally {
                                    if (timer) clearTimeout(timer);
                                  }
                                };
                                if (Platform.OS === 'web') {
                                  void withTimeout('bootstrapI18nLanguage', bootstrapI18nLanguage());
                                  void withTimeout(
                                    'applyMomentLocale',
                                    applyMomentLocale(i18n.language)
                                  );
                                  return;
                                }
                                await withTimeout('bootstrapI18nLanguage', bootstrapI18nLanguage());
                                await withTimeout(
                                  'applyMomentLocale',
                                  applyMomentLocale(i18n.language)
                                );
                              }}
                            >
                              <ThemeScrollbars />
                              <CrashInterceptor
                                onError={(error, errorInfo) => {
                                  console.error('Global crash intercepted:', error, errorInfo);
                                  // Additional custom error handling can be added here
                                }}
                              >
                                <AppContent showUpdateDetail={updated} />
                                {Platform.OS === 'web' && <UpdateToast />}
                              </CrashInterceptor>
                            </PersistGate>
                          </QueryClientProvider>
                        </ToastProvider>
                </UtilitiesProvider>
              </Provider>
            </LocalizationProvider>
          </TamaguiProvider>
      </SafeAreaProvider>
      </GestureHandlerRootView >
    );
}
